/**
 * parts.tsx — byggeklosser til inVPS-illustrasjonene (isometrisk SVG)
 *
 * Rack: server-rack av mørke enheter med LED og sporlinjer. Cube: kube som
 * representerer en VPS-størrelse. Terminal: mørk flate med kommandoprompt.
 * Bruker Box fra components/product/iso.tsx. Kun blå og grå.
 */

import { Box, iso as heroIso } from '@/components/product/iso';

type Project = (x: number, y: number, z: number) => string;

const UNIT: [string, string, string] = ['#6f6f6f', '#525252', '#393939'];

interface RackProps {
  x: number;
  y: number;
  z?: number;
  units?: number;
  project?: Project;
}

/** Server-rack: `units` enheter oppå hverandre, med LED og to sporlinjer per enhet (venstre flate) */
export function Rack({ x, y, z = 0, units = 3, project = heroIso }: RackProps) {
  const w = 2.6;
  const d = 2.4;
  const h = 0.72;
  const gap = 0.08;
  const out = [];
  for (let i = 0; i < units; i++) {
    const zi = z + i * (h + gap);
    out.push(<Box key={`u${i}`} x={x} y={y} z={zi} w={w} d={d} h={h} colors={UNIT} project={project} />);
    const p = (u: number, v: number) => project(x + u * w, y + d, zi + v * h);
    out.push(
      <polygon key={`s1${i}`} points={`${p(0.1, 0.62)} ${p(0.62, 0.62)} ${p(0.62, 0.74)} ${p(0.1, 0.74)}`} fill="#262626" />,
      <polygon key={`s2${i}`} points={`${p(0.1, 0.3)} ${p(0.62, 0.3)} ${p(0.62, 0.42)} ${p(0.1, 0.42)}`} fill="#262626" />,
      <polygon key={`l${i}`} points={`${p(0.74, 0.4)} ${p(0.86, 0.4)} ${p(0.86, 0.6)} ${p(0.74, 0.6)}`} fill="#4589ff" />,
    );
  }
  return <g>{out}</g>;
}

const CUBE_TONES = {
  blue: ['#a6c8ff', '#4589ff', '#0f62fe'],
  deep: ['#4589ff', '#0f62fe', '#0043ce'],
  light: ['#d0e2ff', '#a6c8ff', '#78a9ff'],
} as const;

interface CubeProps {
  x: number;
  y: number;
  z?: number;
  size: number;
  tone?: keyof typeof CUBE_TONES;
  project?: Project;
}

/** Kube (en VPS-størrelse) */
export function Cube({ x, y, z = 0, size, tone = 'blue', project = heroIso }: CubeProps) {
  return <Box x={x} y={y} z={z} w={size} d={size} h={size} colors={[...CUBE_TONES[tone]]} project={project} />;
}

interface TerminalProps {
  x: number;
  y: number;
  z?: number;
  project?: Project;
}

/** Terminal: mørk flate med «>»-prompt, tekstlinjer og markør på toppflaten */
export function Terminal({ x, y, z = 0, project = heroIso }: TerminalProps) {
  const w = 3.4;
  const d = 2.6;
  const h = 0.28;
  const zt = z + h;
  const p = (u: number, v: number) => project(x + u * w, y + v * d, zt);
  const rows = [0.22, 0.4, 0.58, 0.76].map((v, i) => (
    <polygon
      key={i}
      points={`${p(0.28, v)} ${p([0.82, 0.66, 0.9, 0.5][i], v)} ${p([0.82, 0.66, 0.9, 0.5][i], v + 0.07)} ${p(0.28, v + 0.07)}`}
      fill={i === 3 ? '#4589ff' : '#8d8d8d'}
    />
  ));
  return (
    <g>
      <Box x={x} y={y} z={z} w={w} d={d} h={h} colors={['#393939', '#262626', '#161616']} project={project} />
      <polyline
        points={`${p(0.1, 0.2)} ${p(0.18, 0.255)} ${p(0.1, 0.31)}`}
        fill="none"
        stroke="#78a9ff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {rows}
      <polygon points={`${p(0.56, 0.76)} ${p(0.64, 0.76)} ${p(0.64, 0.83)} ${p(0.56, 0.83)}`} fill="#ffffff" />
    </g>
  );
}
